import {buildUrl} from '../routes';
import {imageUrl} from '../../components/utils/image-url';
import {absoluteUrl, EDITION, SITE_NAME, SITE_URL} from './site';
import {SECTIONS, SectionKey} from './sections';

/**
 * Разметка schema.org в JSON-LD — её читают и Google, и Яндекс.
 * Видимый эффект в выдаче даёт BreadcrumbList (хлебные крошки), остальное помогает поисковику
 * понять, о какой сущности страница и как её ещё называют (alternateName).
 */

export type Crumb = {name: string; path: string};

export type JsonLdImage = {
	path: string;
	width?: number | null;
	height?: number | null;
	description?: string | null;
	credits?: string | null;
};

const WEBSITE_ID = `${SITE_URL}/#website`;
const ORGANIZATION_ID = `${SITE_URL}/#organization`;

function websiteNode() {
	return {
		'@type': 'WebSite',
		'@id': WEBSITE_ID,
		url: SITE_URL,
		name: SITE_NAME,
		inLanguage: 'ru',
		description: SECTIONS.index.description,
		publisher: {'@id': ORGANIZATION_ID},
		potentialAction: {
			'@type': 'SearchAction',
			target: {'@type': 'EntryPoint', urlTemplate: `${SITE_URL}/search?q={search_term_string}`},
			'query-input': 'required name=search_term_string',
		},
	};
}

/** Главная → раздел → переданные звенья */
export function sectionCrumbs(section: SectionKey, ...rest: Crumb[]): Crumb[] {
	const crumbs: Crumb[] = [{name: SECTIONS.index.name, path: '/'}];
	if (section !== 'index') crumbs.push({name: SECTIONS[section].name, path: buildUrl(SECTIONS[section].route)});
	return [...crumbs, ...rest];
}

function imageNode(image: JsonLdImage) {
	const url = absoluteUrl(imageUrl(image.path));
	return {
		'@type': 'ImageObject',
		url,
		contentUrl: url,
		width: image.width || undefined,
		height: image.height || undefined,
		caption: image.description || undefined,
		creditText: image.credits || undefined,
	};
}

type PageJsonLd = {
	path: string;
	title: string;
	description: string;
	crumbs: Crumb[];
	pageType?: 'WebPage' | 'CollectionPage' | 'Article';
	/** сущность, которой посвящена страница */
	about?: {name: string; alternateName?: Array<string | null | undefined>; section: SectionKey};
	image?: JsonLdImage | null;
	/** когда страница менялась в последний раз; для Article */
	dateModified?: string | null;
};

const publisherNode = () => ({
	'@type': 'Organization',
	'@id': ORGANIZATION_ID,
	name: SITE_NAME,
	url: SITE_URL,
	logo: {'@type': 'ImageObject', url: absoluteUrl('/icon-512.png'), width: 512, height: 512},
});

const siteRef = () => ({'@type': 'WebSite', '@id': WEBSITE_ID, url: SITE_URL, name: SITE_NAME});

function aboutNode({about, description}: Pick<PageJsonLd, 'about' | 'description'>) {
	if (!about) return undefined;
	const alternateName = about.alternateName?.filter(Boolean) as string[] | undefined;
	return {
		'@type': 'DefinedTerm',
		name: about.name,
		alternateName: alternateName?.length ? alternateName : undefined,
		description,
		inDefinedTermSet: {
			'@type': 'DefinedTermSet',
			name: `${SECTIONS[about.section].name} ${EDITION}`,
			url: absoluteUrl(buildUrl(SECTIONS[about.section].route)),
		},
	};
}

/**
 * Каждый блок — отдельный объект со своим @context: так его читают и Google, и Яндекс
 * (у Яндекса с @graph и ссылками по @id бывают сбои, особенно в хлебных крошках).
 */
export function pageJsonLd(props: PageJsonLd): object[] {
	const {path, title, description, crumbs, pageType = 'WebPage', image, dateModified} = props;
	const url = absoluteUrl(path);
	const imageUrls = image?.path ? [imageNode(image)] : undefined;
	const blocks: Array<Record<string, unknown>> = [];

	if (path === '/') blocks.push(websiteNode());

	if (pageType === 'Article') {
		blocks.push({
			'@type': 'Article',
			'@id': `${url}#article`,
			headline: title.length > 110 ? `${title.slice(0, 107)}…` : title,
			name: title,
			description,
			url,
			inLanguage: 'ru',
			mainEntityOfPage: {'@type': 'WebPage', '@id': url},
			isPartOf: siteRef(),
			image: imageUrls,
			dateModified: dateModified || undefined,
			author: {'@id': ORGANIZATION_ID, '@type': 'Organization', name: SITE_NAME, url: SITE_URL},
			publisher: publisherNode(),
			about: aboutNode(props),
		});
	} else {
		blocks.push({
			'@type': pageType,
			'@id': url,
			url,
			name: title,
			description,
			inLanguage: 'ru',
			isPartOf: siteRef(),
			about: aboutNode(props),
			primaryImageOfPage: imageUrls?.[0],
		});
	}

	if (crumbs.length > 1) {
		blocks.push({
			'@type': 'BreadcrumbList',
			itemListElement: crumbs.map((crumb, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				name: crumb.name,
				item: absoluteUrl(crumb.path),
			})),
		});
	}

	return blocks.map((block) => ({'@context': 'https://schema.org', ...block}));
}
